/**
 * Effective-flag gate for the panel's own surfaces (M7-96, §13).
 *
 * The admin flag catalog (`GET /admin/flags`) is what the operator EDITS;
 * this reads `GET /config/flags`, the caller-scoped EFFECTIVE map, which is
 * what a client is supposed to gate itself on: no `settings.flag.view` grant
 * is needed, deploy-level locks are already applied, and an unknown key is
 * simply absent.
 *
 * FALSE is both the platform default and the fail-safe. While the map is
 * loading, unreadable, errored, or missing the key (older server), the gated
 * surface stays HIDDEN — never greyed, never half-rendered (the M7-37e
 * locked-flag treatment). The server guard stays the authority.
 */

import { useCallback, useMemo } from "react";
import { getEffectiveFlags } from "../api/settingsApi";
import { useApiClient } from "./useApiClient";
import { useQuery } from "./useQuery";

/**
 * Merchant staff accounts — owner ruling 2026-09-06: the capability was never
 * in the plan and is switched OFF platform-wide. Re-enabling is a flag flip
 * (Settings → Feature flags), which is exactly why the screens stay in the
 * codebase behind this key rather than being deleted.
 */
export const MERCHANT_STAFF_FLAG = "merchant_staff";

/**
 * Customer-app ad banners (Wave 14, owner ruling 2026-09-07) — default ON.
 * The whole Ads section (nav item + `/ads` routes) exists only while it is on.
 */
export const CUSTOMER_ADS_FLAG = "customer_ads";

/**
 * Multiple branches per merchant (Wave 16, owner ruling 2026-09-09) — default
 * OFF: one implicit location per store. While it is off the panel shows the
 * store's single location inline and never says "branch"; the branch
 * endpoints, schema and screens stay intact for the flag flip.
 */
export const MULTI_BRANCH_FLAG = "multi_branch";

export interface FeatureFlagGate {
  /** True only for a key the effective map explicitly reports as on. */
  readonly enabled: (flagKey: string) => boolean;
  /**
   * True until the map has been read once (success OR failure). A ROUTE
   * that gates on a flag renders a skeleton during this window rather than
   * flashing "not found" on the way to a screen that does exist (M7-106);
   * `enabled()` still answers false meanwhile, so nothing gated ever fires
   * a request early.
   */
  readonly loading: boolean;
}

/** The effective map as one gate — every flag on a screen shares the read. */
export function useFeatureFlags(): FeatureFlagGate {
  const client = useApiClient();
  // One query key for the whole MAP, not per flag: the response carries every
  // key at once and is ETag-cached server-side, so two gates on one screen
  // cost one small request between them.
  const query = useQuery(() => getEffectiveFlags(client), "effective-flags");
  const flags = query.data?.flags;
  const enabled = useCallback((flagKey: string) => flags?.[flagKey] === true, [flags]);
  return useMemo(() => ({ enabled, loading: query.loading }), [enabled, query.loading]);
}

export function useFeatureFlag(flagKey: string): boolean {
  return useFeatureFlags().enabled(flagKey);
}

export function useMerchantStaffEnabled(): boolean {
  return useFeatureFlag(MERCHANT_STAFF_FLAG);
}

export function useMultiBranchFlag(): boolean {
  return useFeatureFlag(MULTI_BRANCH_FLAG);
}
